import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {FullBotUi} from '../../../api/generated';
import {botApi, RequestStatus, vehicleReimportApi} from "../../../api/apiConfiguration";
import {RootState} from "../../../app/store";

interface BotState {
    bot?: FullBotUi
    botRequestStatus: RequestStatus
    reimportRequestStatus: RequestStatus
}

const initialState: BotState = {
    botRequestStatus: RequestStatus.NONE,
    reimportRequestStatus: RequestStatus.NONE
}

export const fetchBotById = createAsyncThunk(
    'bot/fetchBotById',
    (publicId: string) => botApi.getBotById({publicId})
);

export const reimportVehicles = createAsyncThunk<FullBotUi, string, { rejectValue: string | null }>(
    'bot/reimportVehicles',
    async (publicId: string, {rejectWithValue}) => {
        try {
            return await vehicleReimportApi.reimportVehiclesByPublicBotId({publicId});
        } catch (error: any) {
            const message = error?.response ? await error.response.text() : null;
            return rejectWithValue(message);
        }
    }
);

export const refreshReimportStatus = createAsyncThunk(
    'bot/refreshReimportStatus',
    (publicId: string) => botApi.getBotById({publicId})
);

export const botSlice = createSlice({
    name: 'bot',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(reimportVehicles.fulfilled, (state, {payload}) => {
            state.bot = payload;
            state.botRequestStatus = RequestStatus.RESOLVED
            state.reimportRequestStatus = RequestStatus.RESOLVED
        });
        builder.addCase(reimportVehicles.pending, (state) => {
            state.reimportRequestStatus = RequestStatus.PENDING
        });
        builder.addCase(reimportVehicles.rejected, (state) => {
            state.reimportRequestStatus = RequestStatus.REJECTED
        });
        builder.addCase(refreshReimportStatus.fulfilled, (state, {payload}) => {
            if (state.bot && state.bot.publicId === payload.publicId) {
                state.bot.isReimportFinished = payload.isReimportFinished;
            }
        });
        builder.addCase(fetchBotById.fulfilled, (state, {payload}) => {
            state.bot = payload;
            state.botRequestStatus = RequestStatus.RESOLVED
        });
        builder.addCase(fetchBotById.pending, (state) => {
            state.botRequestStatus = RequestStatus.PENDING
        });
        builder.addCase(fetchBotById.rejected, (state) => {
            state.botRequestStatus = RequestStatus.REJECTED
        });
    }
})

export const _ = botSlice.actions;

export const botSelector = (state: RootState): FullBotUi | undefined => state.bot.bot;
export const botRequestStatusSelector = (state: RootState): RequestStatus => state.bot.botRequestStatus;
export const reimportRequestStatusSelector = (state: RootState): RequestStatus => state.bot.reimportRequestStatus;
